import { useState } from 'react';
import {
    TextField,
    Autocomplete,
    Grid,
    Typography,
    Divider,
    IconButton,
    InputAdornment,
    LinearProgress,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'components/lib';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import Styles from './MobileSignupForm.module.scss'
import CaptchaField from 'components/global/CaptchaField/CaptchaField';

// Password strength assessment
const getPasswordStrength = (password) => {
    if (!password) return 0;

    let strength = 0;

    if (password.length > 0) strength += 10
    // Length check
    if (password.length >= 8) strength += 15;

    // Contains uppercase
    if (/[A-Z]/.test(password) && password.length >= 8) strength += 25;

    // Contains number
    if (/\d/.test(password) && password.length >= 8) strength += 25;

    // Contains special character
    if (/[^A-Za-z0-9]/.test(password) && password.length >= 8) strength += 25;

    return strength;
};

const getStrengthLabel = (strength) => {
    if (strength === 0) return 'No Password';
    if (strength <= 15) return 'Too Weak';
    if (strength <= 25) return 'Weak';
    if (strength <= 50) return 'Fair';
    if (strength <= 75) return 'Good';
    return 'Strong';
};

const getStrengthColor = (strength) => {
    if (strength <= 25) return '#f44336'; // red
    if (strength <= 50) return '#ff9800'; // orange
    if (strength <= 75) return '#2196f3'; // blue
    return '#4caf50'; // green
};

export function MobileSignupForm({
    values,
    onChange,
    onSubmit,
    onCheckboxChange,
    captcha,
    onCaptchaChange,
    onRefreshCaptcha,
    loading,
    countryCodes,
    signUpMethod,
    setSignUpMethod,
    redirectLink = ''
}) {

    const navigate = useNavigate();
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const phoneRegex = /^\s*(\d\s*){7,15}\s*$/; // Valid phone number
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Valid email
    const nameRegex = /^[A-Za-z]+$/; // Only letters
    const captchaRegex = /^-?\d+$/; //Only -ve/+ve integers

    const passwordStrength = getPasswordStrength(values.password);
    const strengthLabel = getStrengthLabel(passwordStrength);
    const strengthColor = getStrengthColor(passwordStrength);

    const validate = () => {
        const newErrors = {};

        // First name: required + only letters
        if (!values.firstName?.trim()) {
            newErrors.firstName = 'First name is required';
        } else if (!nameRegex.test(values.firstName)) {
            newErrors.firstName = 'First name can only contain letters';
        } else if (values.firstName?.trim().length > 30) {
            newErrors.firstName = 'First name cannot exceed 30 characters.'
        }
        if (!captcha?.input?.trim()) {
            newErrors.captcha = "Captcha is required";
        } else if (!captchaRegex.test(captcha.input.trim())) {
            newErrors.captcha = "Captcha must be a number";
        }
        if (signUpMethod === 'phone') {
            if (!phoneRegex.test(values.phoneNumber)) {
                newErrors.phoneNumber = 'Enter a valid phone number';
            }
        } else {
            if (!emailRegex.test(values.email)) {
                newErrors.email = 'Enter a valid email address';
            }

            if (!values.password || values.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters long';
            } else if (!/[A-Z]/.test(values.password)) {
                newErrors.password = 'Password must contain at least one uppercase letter';
            } else if (!/[a-z]/.test(values.password)) {
                newErrors.password = 'Password must contain at least one lowercase letter';
            } else if (!/\d/.test(values.password)) {
                newErrors.password = 'Password must contain at least one number';
            } else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(values.password)) {
                newErrors.password = 'Password must contain at least one special character';
            }

            if (values.password !== values.confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }
        }

        return newErrors;
    };

    const handleSubmit = () => {
        const formErrors = validate();
        setErrors(formErrors);

        if (signUpMethod === 'phone') {
            setTouched({
                firstName: true,
                phoneNumber: true,
                captcha: true
            });
        } else {
            setTouched({
                firstName: true,
                email: true,
                password: true,
                confirmPassword: true,
                captcha: true
            });
        }

        if (Object.keys(formErrors).length === 0) {
            onSubmit({ ...values });
        }
    };

    const handleBlur = (field) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        setErrors(validate());
    };

    const switchToEmailSignup = () => {
        setSignUpMethod('email');
        setTouched({});
        setErrors({});
    };

    const switchToPhoneSignup = () => {
        setSignUpMethod('phone');
        setTouched({});
        setErrors({});
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div id='formContainer' className={Styles.formContainer}>
            {signUpMethod === 'phone' ? (
                <>
                    <Typography
                        variant="h5"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '24px',
                            fontWeight: 600,
                            color: '#1450a0'
                        }}
                    >
                        <PhoneIcon sx={{ marginRight: '8px' }} />
                        Signup with Mobile
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            marginTop: '4px',
                            marginBottom: '16px',
                            color: 'text.secondary',
                            textAlign: 'center'
                        }}
                    >
                        Create your account using a phone number.
                    </Typography>
                    <div id='nameInputContainer' className={Styles.formFieldContainer}>
                        <TextField
                            label="First Name"
                            type="text"
                            name="firstName"
                            value={values.firstName}
                            onChange={onChange}
                            onBlur={() => handleBlur('firstName')}
                            onKeyDown={handleKeyDown}
                            error={touched.firstName && !!errors.firstName}
                            helperText={touched.firstName && errors.firstName}
                            required
                            fullWidth
                        />
                    </div>
                    <div id='phoneInputContainer' className={Styles.formFieldContainer}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Autocomplete
                                    options={countryCodes}
                                    getOptionLabel={(option) => `${option.isoCode3} +${option.countryCodes[0]}`}
                                    value={countryCodes.find(option => option.countryCodes[0] === values.countryCode) || null}
                                    onChange={(event, newValue) => {
                                        onChange({ target: { name: 'countryCode', value: newValue?.countryCodes[0] || '' } });
                                    }}
                                    disableClearable
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Country"
                                            name="countryCode"
                                            required
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <TextField
                                    label="Phone Number"
                                    name="phoneNumber"
                                    type="tel"
                                    value={values.phoneNumber}
                                    onChange={onChange}
                                    placeholder="1234567890"
                                    onBlur={() => handleBlur('phoneNumber')}
                                    onKeyDown={handleKeyDown}
                                    error={touched.phoneNumber && !!errors.phoneNumber}
                                    helperText={touched.phoneNumber && errors.phoneNumber}
                                    required
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </div>

                    <div id='marketingCheckboxContainer' className={Styles.formFieldContainer}>
                        <label className={Styles.marketingLabel}>
                            <input className={Styles.marketingCheckBox}
                                type="checkbox"
                                checked={values.marketing}
                                onChange={(e) => onCheckboxChange(e)}
                            />
                            Get Marketing and other important updates
                        </label>
                    </div>
                    {!!captcha?.image && (
                        <div className={Styles.formFieldContainer}>
                            <CaptchaField
                                captcha={captcha}
                                onRefreshCaptcha={onRefreshCaptcha}
                                onCaptchaChange={onCaptchaChange}
                                onBlur={handleBlur}
                                error={touched.captcha && !!errors.captcha}
                                helperText={touched.captcha && errors.captcha}
                            />
                        </div>
                    )}

                    <AivagamButton
                        id="continueButton"
                        buttonText="Create Account"
                        onClick={handleSubmit}
                        classStyles={{
                            width: "50%",
                            fontSize: "1em",
                            padding: "0.5em",
                            marginBottom: "0.6em",
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        type="confirm"
                        disabled={loading}
                    />

                    <div id='dividerContainer' className={Styles.dividerContainer}>
                        <Divider className={Styles.dividerLine} />
                        <Typography className={Styles.dividerText}>OR</Typography>
                        <Divider className={Styles.dividerLine} />
                    </div>

                    <AivagamButton
                        id="switchToEmailButton"
                        buttonText="Sign up with Email"
                        onClick={switchToEmailSignup}
                        classStyles={{
                            width: "50%",
                            fontSize: "1em",
                            padding: "0.5em",
                            marginBottom: "0.6em",
                            backgroundColor: "#1450a0",
                            color: "#ffffff",
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                    />
                </>
            ) : (
                <>
                    <Typography
                        variant="h5"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '24px',
                            fontWeight: 600,
                            color: '#1450a0'
                        }}
                    >
                        <EmailIcon sx={{ marginRight: '8px' }} />
                        Signup with Email
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            marginTop: '4px',
                            marginBottom: '16px',
                            color: 'text.secondary',
                            textAlign: 'center'
                        }}
                    >
                        Get started by creating an account with your email.
                    </Typography>

                    <div id='nameInputContainer' className={Styles.formFieldContainer}>
                        <TextField
                            label="First Name"
                            type="text"
                            name="firstName"
                            value={values.firstName}
                            onChange={onChange}
                            onBlur={() => handleBlur('firstName')}
                            onKeyDown={handleKeyDown}
                            error={touched.firstName && !!errors.firstName}
                            helperText={touched.firstName && errors.firstName}
                            required
                            fullWidth
                        />
                    </div>
                    <div id='emailInputContainer' className={Styles.formFieldContainer}>
                        <TextField
                            label="Email"
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={onChange}
                            onBlur={() => handleBlur('email')}
                            onKeyDown={handleKeyDown}
                            error={touched.email && !!errors.email}
                            helperText={touched.email && errors.email}
                            required
                            fullWidth
                        />
                    </div>

                    <div id='passwordInputContainer' className={Styles.formFieldContainer}>
                        <TextField
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={values.password || ''}
                            onChange={onChange}
                            onBlur={() => handleBlur('password')}
                            onKeyDown={handleKeyDown}
                            error={touched.password && !!errors.password}
                            helperText={touched.password && errors.password ? errors.password : 'Must be at least 8 characters, include uppercase, number, and special character'}
                            required
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {values.password && (
                            <div id='passwordStrengthContainer' className={Styles.passwordStrengthContainer}>
                                <div id='passwordStrength' className={Styles.passwordStrength}>
                                    <Typography variant="caption">Password Strength: {strengthLabel}</Typography>
                                    <Typography variant="caption" style={{ color: strengthColor }}>{passwordStrength}%</Typography>
                                </div>
                                <LinearProgress
                                    variant="determinate"
                                    value={passwordStrength}
                                    sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        backgroundColor: 'rgba(0,0,0,0.08)',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: strengthColor,
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <div id='confirmPassowordInputContainer' className={Styles.formFieldContainer}>
                        <TextField
                            label="Confirm Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="confirmPassword"
                            value={values.confirmPassword || ''}
                            onChange={onChange}
                            onBlur={() => handleBlur('confirmPassword')}
                            onKeyDown={handleKeyDown}
                            error={touched.confirmPassword && !!errors.confirmPassword}
                            helperText={touched.confirmPassword && errors.confirmPassword}
                            required
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle confirm password visibility"
                                            onClick={handleClickShowConfirmPassword}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>
                    {!!captcha?.image && (
                        <div className={Styles.formFieldContainer}>
                            <CaptchaField
                                captcha={captcha}
                                onRefreshCaptcha={onRefreshCaptcha}
                                onCaptchaChange={onCaptchaChange}
                                onBlur={handleBlur}
                                error={touched.captcha && !!errors.captcha}
                                helperText={touched.captcha && errors.captcha}
                            />
                        </div>
                    )}

                    <div id='marketingCheckboxContainer' className={Styles.formFieldContainer}>
                        <label className={Styles.marketingLabel}>
                            <input className={Styles.marketingCheckBox}
                                type="checkbox"
                                checked={values.marketing}
                                onChange={onCheckboxChange}
                            />
                            Get Marketing and other important updates
                        </label>
                    </div>

                    <AivagamButton
                        id="signupEmailButton"
                        buttonText="Create Account"
                        onClick={handleSubmit}
                        classStyles={{
                            width: "50%",
                            fontSize: "1em",
                            padding: "0.5em",
                            marginBottom: "0.6em",
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        type="confirm"
                        disabled={loading}
                    />

                    <div id='dividerContainer' className={Styles.dividerContainer}>
                        <Divider className={Styles.dividerLine} />
                        <Typography className={Styles.dividerText}>OR</Typography>
                        <Divider className={Styles.dividerLine} />
                    </div>

                    <AivagamButton
                        id="switchToPhoneButton"
                        buttonText="Sign up with Phone Number"
                        onClick={switchToPhoneSignup}
                        disabled={true} //TODO: temp measure until otp is fixed
                        classStyles={{
                            width: "50%",
                            fontSize: "1em",
                            padding: "0.5em",
                            marginBottom: "0.6em",
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    />
                </>
            )}

            <div id='signInLink' className={Styles.signInLink}>
                Already Registered?
                <p
                    onClick={() => {
                        navigate('/signin', {
                            state: { redirectTo: redirectLink }
                        })
                    }}
                    style={{ color: 'blue', cursor: 'pointer', display: 'inline', textDecoration: 'underline' }}>
                    Sign In
                </p>
            </div>
        </div >
    );
}
